import { useQueries, useQuery } from '@tanstack/react-query';
import { TimelineLayoutApi } from '@/lib/api/roadmapping/timeline-layout';
import { roadmappingQueryKeys } from '@/lib/query-keys/roadmapping';

export const useTimelineHeaderConfig = ({
  viewId,
  visualizationId,
  headerFieldId,
  parentFieldId = null,
  parentHeaderId = null,
}: {
  viewId: number;
  visualizationId: number;
  headerFieldId: string | null;
  parentFieldId?: string | null;
  parentHeaderId?: string | null;
}) => {
  return useQuery({
    queryKey: roadmappingQueryKeys.timelineLayout.headerConfig({
      viewId,
      visualizationId,
      headerFieldId: headerFieldId || '',
      parentFieldId,
      parentHeaderId,
    }),
    queryFn: () => {
      return TimelineLayoutApi.fetchHeaderConfig({
        viewId,
        visualizationId,
        headerFieldId: headerFieldId as string,
      });
    },
    enabled: !!headerFieldId,
    refetchOnMount: false,
  });
};

type HeaderConfig = {
  orderedHeaderIds: string[];
  hiddenHeaderIds: string[];
};

export const useTimelineSubHeaderConfigs = ({
  viewId,
  visualizationId,
  headerFieldId,
  parentFieldId,
  headers,
}: {
  viewId: number;
  visualizationId: number;
  headerFieldId: string | null;
  parentFieldId?: string | null;
  headers: { id: string }[];
}) => {
  return useQueries({
    queries: headers.map((header) => ({
      queryKey: roadmappingQueryKeys.timelineLayout.headerConfig({
        viewId,
        visualizationId,
        headerFieldId: headerFieldId || '',
        parentFieldId,
        parentHeaderId: header.id,
      }),
      queryFn: () => {
        return TimelineLayoutApi.fetchHeaderConfig({
          viewId,
          visualizationId,
          headerFieldId: headerFieldId as string,
          parentFieldId,
          parentHeaderId: header.id,
        });
      },
      enabled: !!headerFieldId && !!parentFieldId && !!header.id,
    })),
    combine: (results): Record<string, HeaderConfig> => {
      const configMap: Record<string, HeaderConfig> = {};
      results.forEach((result) => {
        if (result.status === 'success' && result.data.parentHeaderId) {
          configMap[result.data.parentHeaderId] = {
            orderedHeaderIds: result.data.orderedHeaderIds,
            hiddenHeaderIds: result.data.hiddenHeaderIds,
          };
        }
      });
      return configMap;
    },
  });
};
